import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Search, Users, Building2, CheckCircle2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * UserPickerSheet — shared modal used for:
 *   - "Tag users" in a reel (kind="tag")
 *   - "Invite for collaboration" (kind="collab") to brand/creator accounts
 *
 * Props:
 *   open (bool)
 *   onClose()
 *   onDone(selectedUsers: [{id, username, name, avatar}])
 *   kind: "tag" | "collab"
 *   title: string
 *   multi (bool) — allow multi-select (default true)
 *   brandsOnly (bool) — filter to brand accounts only (default false)
 */
export default function UserPickerSheet({ open, onClose, onDone, kind = "tag", title, multi = true, brandsOnly = false }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const query = q.trim() || "a";
    const url = `/users/search?q=${encodeURIComponent(query)}&limit=30${brandsOnly ? "&brands_only=1" : ""}`;
    const t = setTimeout(() => {
      api.get(url).then(r => setUsers(r.data || [])).catch(() => setUsers([])).finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [open, q, brandsOnly]);

  useEffect(() => { if (!open) { setSelected({}); setQ(""); } }, [open]);

  const toggle = (u) => setSelected(s => {
    if (!multi) return { [u.id]: u };
    const n = { ...s };
    if (n[u.id]) delete n[u.id]; else n[u.id] = u;
    return n;
  });

  const chosen = Object.values(selected);

  const confirm = () => {
    onDone(chosen);
    onClose();
  };

  if (!open) return null;

  const heading = title || (kind === "collab" ? "Invite for Collaboration" : "Tag Hifireels users");
  const cta = kind === "collab" ? "Send Collab Invite" : "Tag Users";

  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:26}}
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl bg-[#0B0F19] border border-white/15 max-h-[80vh] flex flex-col"
          data-testid={`user-picker-${kind}`}>
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              {kind === "collab" ? <Building2 className="w-5 h-5 text-[#FF7A00]"/> : <Users className="w-5 h-5 text-[#00E5FF]"/>}
              <h3 className="font-outfit font-extrabold text-lg">{heading}</h3>
            </div>
            <button onClick={onClose} data-testid={`user-picker-${kind}-close`} className="p-1.5 rounded-full bg-white/8">
              <X className="w-4 h-4"/>
            </button>
          </div>
          <div className="p-3">
            <div className="relative">
              <Search className="w-4 h-4 text-white/50 absolute left-3 top-1/2 -translate-y-1/2"/>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={kind === "collab" ? "Search brand or real user @username…" : "Search users by @username or name…"}
                data-testid={`user-picker-${kind}-search`}
                className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white/5 border border-white/10 outline-none text-sm focus:border-[#00E5FF]"/>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid={`user-picker-${kind}-list`}>
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]"/></div>
            ) : users.length === 0 ? (
              <div className="text-center py-6 text-white/50 text-sm">No users found</div>
            ) : users.map(u => {
              const on = !!selected[u.id];
              return (
                <button key={u.id} onClick={() => toggle(u)}
                  data-testid={`user-picker-${kind}-user-${u.id}`}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-2xl active:scale-[0.99] ${on ? "bg-[#00E5FF]/10 border border-[#00E5FF]/40" : "hover:bg-white/5 border border-transparent"}`}>
                  <img src={u.avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10"/>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-bold truncate flex items-center gap-1">
                      {u.name || u.username}
                      {u.is_brand && <span className="px-1.5 py-0.5 rounded-full bg-[#FF7A00]/25 text-[#FF7A00] text-[9px] font-black uppercase">Brand</span>}
                    </div>
                    <div className="text-[10px] text-white/50 truncate">@{u.username} · {u.followers || 0} followers</div>
                  </div>
                  {on && <CheckCircle2 className="w-5 h-5 text-[#00E5FF] flex-shrink-0"/>}
                </button>
              );
            })}
          </div>
          <div className="p-3 border-t border-white/10">
            <button onClick={confirm} disabled={chosen.length === 0}
              data-testid={`user-picker-${kind}-confirm`}
              className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7] text-black font-outfit font-extrabold disabled:opacity-40 active:scale-95">
              {cta} {chosen.length > 0 ? `(${chosen.length})` : ""}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
